import React, { useState, useRef, useEffect } from "react";
import { 
  Grid, 
  Container,
  Table,
  TableBody,
  Typography,
  CardContent,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  Button,
} from '@mui/material';
import { CommanTableContain, PaginationContain, StyledTableBox } from "../../style/Admin/AddUserTable"; 
import { Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { uploadUserFile, handleApiError } from '../../services/userServices';
import { StyledBox } from '../../style/Admin/AddUser';
import * as XLSX from 'xlsx';
import { Colors } from "../../style/theme/index.js";
import DashboardHeader from "../../components/Common/DashboardHeader";

const phoneRegex = /^[6-9]\d{9}$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const AddUser = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileDetails, setFileDetails] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const headerRefs = useRef([]);
  const [columnWidths, setColumnWidths] = useState([]);
    
  // Define the exact headers that must be present in the Excel file
  const requiredHeaders = [
    { id: "User Id", label: "User Id" },
    { id: "Name", label: "Name" },
    { id: "Phone Number", label: "Phone Number" },
    { id: "Email Id", label: "Email Id" },
    { id: "Status", label: "Status" }
  ];

  useEffect(() => {
    if (headerRefs.current.length > 0) {
      const widths = headerRefs.current.map(ref => 
        ref ? ref.getBoundingClientRect().width : 0
      );
      setColumnWidths(widths);
    }
  }, [previewData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    if ([10, 25, 50, 100].includes(value)) {
      setRowsPerPage(value);
    }
    setPage(0);
  };

  // Function to validate headers in the Excel/CSV file
  const validateHeaders = (headers) => {
    const missingHeaders = [];
    
    // Check if all required headers exist in the uploaded file
    requiredHeaders.forEach(requiredHeader => {
      if (!headers.includes(requiredHeader.id)) {
        missingHeaders.push(requiredHeader.id);
      }
    });
    
    // Check for any extra headers that aren't in our required list
    const requiredHeaderIds = requiredHeaders.map(h => h.id);
    const extraHeaders = headers.filter(header => !requiredHeaderIds.includes(header));
    
    return { missingHeaders, extraHeaders };
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    e.target.value = '';

    const fileType = selectedFile.type;
    const fileName = selectedFile.name;
    const fileExtension = fileName.split('.').pop().toLowerCase();

    if (
      (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || fileExtension === 'xlsx') ||
      (fileType === 'application/vnd.ms-excel' || fileExtension === 'xls') ||
      (fileType === 'text/csv' || fileExtension === 'csv')
    ) {
      setFile(selectedFile);
      setFileDetails({
        name: selectedFile.name,
        size: (selectedFile.size / 1024).toFixed(2) + ' KB',
        type: selectedFile.type
      });

      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        
        // Get the headers from the first row
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const headers = [];
        
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
          if (cell && cell.v) {
            headers.push(cell.v.toString().trim());
          }
        }
        
        // Validate headers
        const { missingHeaders, extraHeaders } = validateHeaders(headers);
        
        if (missingHeaders.length > 0) {
          toast.error(`Missing required headers: ${missingHeaders.join(', ')}`);
          setFile(null);
          setFileDetails(null);
          return;
        }
        
        if (extraHeaders.length > 0) {
          toast.warning(`Extra headers detected: ${extraHeaders.join(', ')}. These will be ignored.`);
        }
        
        // Continue with data parsing if headers are valid
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        let actualRowIndex = 1; // Start from 1 to account for header row
        const seenUserIds = new Set();
        const seenPhones = new Set();
        const seenEmails = new Set();
        
        const validatedData = jsonData.map((row) => {
          // Skip completely empty rows
          if (!row['User Id'] && !row['Name'] && !row['Phone Number'] && 
              !row['Email Id'] && !row['Status']) {
            return null;
          }
          
          actualRowIndex++;
          const phone = row['Phone Number']?.toString();
          const email = row['Email Id']?.toString();
          const userId = row['User Id']?.toString();
          const status = row['Status']?.toString();
          
          // Check for duplicates
          if (userId && seenUserIds.has(userId)) {
            toast.error(`Duplicate User ID "${userId}" found in row ${actualRowIndex}`);
            return null;
          }
          
          if (phone && seenPhones.has(phone)) {
            toast.error(`Duplicate Phone Number "${phone}" found in row ${actualRowIndex}`);
            return null;
          }
          
          if (email && seenEmails.has(email)) {
            toast.error(`Duplicate Email "${email}" found in row ${actualRowIndex}`);
            return null;
          }
          
          // Add to seen sets if valid
          if (userId) seenUserIds.add(userId);
          if (phone) seenPhones.add(phone);
          if (email) seenEmails.add(email);
          
          // Validate all required fields
          if (!userId || userId.trim() === '') {
            toast.error(`User ID is required in row ${actualRowIndex}`);
            return null;
          }
          
          if (!phone || !phoneRegex.test(phone)) {
            toast.error(`Invalid phone number in row ${actualRowIndex}`);
            return null;
          }
          
          if (!email || !emailRegex.test(email)) {
            toast.error(`Invalid email in row ${actualRowIndex}`);
            return null;
          }
          
          if (!status || !['active', 'inactive'].includes(status.trim().toLowerCase())) {
            toast.error(`Status must be either 'active' or 'inactive' in row ${actualRowIndex}`);
            return null;
          }
          
          if (!row['Name'] || row['Name'].toString().trim() === '') {
            toast.error(`Name is required in row ${actualRowIndex}`);
            return null;
          }
          
          return {
            user_id: row['User Id'],
            name: row['Name'],
            phone_no: row['Phone Number'],
            email_id: row['Email Id'],
            status: row['Status']
          };
        }).filter(row => row !== null);

        if (validatedData.length === 0) {
          setFile(null);
          setFileDetails(null);
          return;
        }

        setPreviewData(validatedData);
      };
      reader.readAsArrayBuffer(selectedFile);
    } else {
      toast.error('Please upload only Excel or CSV files');
    }
  };

  const handleUpload = async () => {
    if (!file || !previewData) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const formattedData = {
        users: previewData.map(row => ({
          userId: row.user_id?.toString(),
          name: row.name?.toString(),
          phoneNo: row.phone_no?.toString(),
          emailId: row.email_id?.toString(),
          status: row.status?.toString()
        })),
        offset: 10,
        limit: 10,
      };

      const data = await uploadUserFile(formattedData);

      if (data.success) {
        toast.success(`Users uploaded successfully`);
        setFile(null);
        setFileDetails(null);
        setPreviewData(null);
      }
    } catch (error) {
      toast.error(error.message ?? "Internal Server Error");
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      requiredHeaders.map(header => header.id)  // Use the exact header names
    ]);
    
    ws['!cols'] = [
      { width: 15 }, // User Id
      { width: 20 }, // Name
      { width: 15 }, // Phone Number
      { width: 25 }, // Email Id
      { width: 10 }  // Status
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'user_template.xlsx');
  };

  return (
    <StyledBox className="addUserContainer">
      <DashboardHeader title="Add Users" />
      <Container maxWidth="lg">
        <ToastContainer />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledBox className="styledCard">
              <StyledBox className="cardHeader">
                <StyledBox className="styledTitle" component="h5" variant="h5">
                  User Management
                </StyledBox>
              </StyledBox>
              <CardContent>
                <StyledBox className="uploadDescription" component="p">
                  Upload an Excel or CSV file containing user data
                </StyledBox>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={5}>
                    <StyledBox className="requirementsTitle">Required columns:</StyledBox>
                    <StyledBox className="requirementsList">
                      <Typography component="ul">
                        {requiredHeaders.map((header, index) => (
                          <li key={index}>{header.id} (required - exact name needed)</li>
                        ))}
                      </Typography>
                    </StyledBox>
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadTemplate}
                      fullWidth
                      variant="outlined"
                    >
                      Download Template
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <StyledBox className="uploadBox">
                      <StyledBox className="uploadTitle">
                        {file ? file.name : 'Select File'}
                      </StyledBox>
                      {fileDetails && (
                        <StyledBox className="fileDetailsBox">
                          <Typography>
                            Size: {fileDetails.size} | Type: {
                              fileDetails.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' :
                              fileDetails.type === 'application/vnd.ms-excel' ? 'xls' :
                              fileDetails.type === 'text/csv' ? 'csv' :
                              fileDetails.type.split('/')[1]
                            }
                          </Typography>
                        </StyledBox>
                      )}
                      <Button
                        component="label"
                        startIcon={<UploadIcon />}
                        fullWidth
                        variant="contained"
                      >
                        Browse Files
                        <input
                          type="file"
                          hidden
                          accept=".xlsx,.xls,.csv"
                          onChange={handleFileChange}
                        />
                      </Button>
                      <Button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        variant="contained"
                        fullWidth
                        sx={{
                          marginTop: '16px',
                          backgroundColor: !file || uploading ? '#ccc' : '#009000',
                          '&:hover': {
                            backgroundColor: !file || uploading ? '#ccc' : '#4fc44f',
                          }
                        }}
                      >
                        {uploading ? 'Uploading...' : 'Upload Users'}
                      </Button>
                    </StyledBox>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledBox>
          </Grid>
        </Grid>
      </Container>
       
      {previewData && (
        <>
          <StyledTableBox className="cardHeader">
            Preview Users
          </StyledTableBox>
        
          <CommanTableContain>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {requiredHeaders.map((header, index) => (
                    <TableCell
                      key={header.id}
                      ref={(el) => (headerRefs.current[index] = el)}
                    >
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell>{row.user_id}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.phone_no}</TableCell>
                      <TableCell>{row.email_id}</TableCell>
                      <TableCell>{row.status}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CommanTableContain>
          <PaginationContain>
            <TablePagination
              component="div"
              count={previewData ? previewData.length : 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </PaginationContain>
        </>
      )}
    </StyledBox>
  );
};

export default AddUser;